'use client';

import type {
  Breadcrumb,
  ProductDetailData,
} from '@eliadmin/core/src/api/products/types/product-types';
import { BREADCRUMBS_CONFIG } from '@eliadmin/core/src/constants/breadcrumbs-config';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { arePathsEqual, getFlatBreadcrumbs } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import { FiHome } from 'react-icons/fi';

export type BreadcrumbsProps = {
  className?: {
    wrapper?: string;
    list?: string;
    item?: string;
    itemActive?: string;
    link?: string;
  };
  homeText?: string;
  breadcrumb: Breadcrumb;
  product?: ProductDetailData;
};

export function Breadcrumbs({ breadcrumb, className, product }: BreadcrumbsProps) {
  const pathname = usePathname();

  const breadcrumbs = getFlatBreadcrumbs(breadcrumb);

  if (BREADCRUMBS_CONFIG.noDisplay.filter((el: string) => el === pathname).length) {
    return <nav className="hidden"></nav>;
  }

  return (
    <nav className={cn('w-full rounded-none p-0', 'ea-breadcrumbs', className?.wrapper)}>
      <ol
        className={cn(
          'flex flex-row flex-wrap items-center space-x-1 capitalize text-[hsl(var(--primary))]',
          'ea-breadcrumbs__list',
          className?.list,
        )}
      >
        {breadcrumbs.map((breadcrumb, index) => (
          <li key={breadcrumb.slug} className="w-auto">
            <span
              className={cn('text-[#EDEDED]', className?.item, {
                hidden: index === 0,
              })}
            >
              /{' '}
            </span>
            <span
              className={cn(
                {
                  [cn(
                    'pointer-events-none font-light text-primary',
                    'ea_breadcrumbs__item--active',
                    className?.itemActive,
                  )]: arePathsEqual(pathname, breadcrumb.slug),
                },
                'ea_breadcrumbs__item',
                className?.item,
              )}
            >
              <Link
                href={breadcrumb.slug ?? '#'}
                className={cn('ea-breadcrumbs__link', className?.link)}
                prefetch={false}
              >
                {breadcrumb.name}
              </Link>
            </span>
          </li>
        ))}
        {product && (
          <li className="flex w-auto items-center gap-1">
            <span aria-hidden="true" className={cn('text-[#EDEDED]')}>
              /{' '}
            </span>
            <Link
              href={product.slug ?? '#'}
              className={cn('ea-breadcrumbs__link font-normal text-[#999999]', className?.link)}
              prefetch={false}
            >
              {product.name}
            </Link>
          </li>
        )}
      </ol>
    </nav>
  );
}
